import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchComponent from './SearchInput';
import EnhancedTable from './CoinTable';
import CoinTableAPY from './CoinTableAPY';
import DemoCoinTable from './DemoCoinTable';

import cryptosImg from '../../../assets/updated/asset_wallet/crypto-04.png';
import cryptoTreasuryImg from '../../../assets/updated/asset_wallet/crypto-treasury.png';
import fiatImg from '../../../assets/updated/asset_wallet/fiat-logo.png';
import aiInvestmentLogo from '../../../assets/updated/asset_wallet/ai-engine.png';
import bitcoinYayLogo from '../../../assets/updated/main-btcy-logo.svg';

// Define the makeStyles hook
const useStyles = makeStyles((theme) => ({
  container: {
    padding: '24px',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    marginTop: '24px',
    minHeight: '682px',
    [theme.breakpoints.down('md')]: {
      border: 'none !important',
    },
  },
  heading: {
    fontSize: '24px !important',
    fontWeight: `600 !important`,
    color: theme.palette.text.primary,
    marginBottom: '16px',
  },
  row: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: '16px',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
  },
  leftDiv: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '20px',
  },
  rightDiv: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flex: 1,
      width: '100%',
      marginTop: '20px',
    },
  },
  searchComponent: {
    [theme.breakpoints.down('md')]: {
      order: 1,
    },
  },
  checkboxLabel: {
    marginLeft: '16px',
    '& .Mui-checked': {
      color: `${theme.palette.text.primary} !important`,
    },
    [theme.breakpoints.down('md')]: {
      order: 0,
    },
  },
}));

const CoinBreakdown = ({ selectedValue, setupdatePlanMode, currentPath }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const effectivePath = currentPath || pathname;

  const [searchQuery, setSearchQuery] = useState('');
  const [hideAssets, setHideAssets] = useState(true);

  const handleSearchChange = (event) => setSearchQuery(event.target.value);
  const handleCheckboxChange = (event) => setHideAssets(event.target.checked);

  const handlePlanChange = (plan, cryptocurrencies) => {
    setupdatePlanMode(true);
    localStorage.setItem('CurrentPlan', plan);
    localStorage.setItem(
      'CurrentPlanCurrencies',
      JSON.stringify(cryptocurrencies)
    );
  };

  // Derive active section from the path (single source of truth)
  const derivedSelected = (() => {
    switch (effectivePath) {
      case '/wallet/ai-engine-investment':
        return 'AI Engine Investment';
      case '/wallet/fiat':
        return 'Fiat';
      case '/wallet/nugget':
        return 'Bitcoin Yay';
      case '/wallet/smart-crypto':
        return 'Smart Crypto';
      case '/wallet/iusd+':
        return 'IUSD+';
      case '/wallet/cryptos':
      case '/wallet/crypto':
        return 'Cryptos';
      default:
        return selectedValue || 'Overview';
    }
  })();

  const showSectionImage =
    derivedSelected !== 'Overview' && effectivePath !== '/wallet/nugget';

  const imageMap = {
    '/wallet/fiat': { src: fiatImg, alt: 'Fiat / Cash', height: 60 },
    '/wallet/ai-engine-investment': {
      src: aiInvestmentLogo,
      alt: 'AI Engine Investment',
      height: 56,
    },
    '/wallet/smart-crypto': {
      src: cryptoTreasuryImg,
      alt: 'Crypto Treasury',
      height: 40,
    },
    '/wallet/iusd+': {
      src: cryptoTreasuryImg,
      alt: 'Crypto Treasury',
      height: 40,
    },
  };

  const sectionImg =
    imageMap[effectivePath] || { src: cryptosImg, alt: 'Cryptos', height: 40 };

  return (
    <Box className={classes.container}>
      <Typography className={classes.heading}>My Assets</Typography>

      <Box className={classes.row}>
        <Box className={classes.rightDiv}>
          <span className={classes.searchComponent}>
            <SearchComponent
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </span>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox checked={hideAssets} onChange={handleCheckboxChange} />
            }
            label="Hide assets <1 USD"
          />
        </Box>
      </Box>

      <Box>
        {selectedValue === 'Overview' ? (
          ''
        ) : (
          <Box
            className={classes.leftDiv}
            sx={{
              gap: effectivePath === '/wallet/nugget' ? '12px' : undefined,
            }}
          >
            {showSectionImage && (
              <img
                src={sectionImg.src}
                alt={sectionImg.alt}
                style={{
                  height: `${sectionImg.height}px`,
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            )}

            {effectivePath === '/wallet/fiat' && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#8BB376 !important',
                  fontSize: '20px',
                  fontStyle: 'italic',
                }}
              >
                Fiat / Cash
              </Typography>
            )}

            {(effectivePath === '/wallet/smart-crypto' ||
              effectivePath === '/wallet/iusd+') && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#F3AF0E !important',
                  fontSize: '20px',
                  fontStyle: 'italic',
                }}
              >
                Crypto Treasury
              </Typography>
            )}

            {effectivePath === '/wallet/ai-engine-investment' && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#BFBFBF !important',
                  fontSize: '20px',
                  fontStyle: 'italic',
                }}
              >
                AI Engine Investment
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {currentPath === '/wallet/iusd+' ? (
        <CoinTableAPY
          searchQuery={searchQuery}
          hideAssets={hideAssets}
          selectedValue={derivedSelected}
          setupdatePlanMode={setupdatePlanMode}
          onPlanChange={handlePlanChange}
        />
      ) : pathname === '/wallet/demo-smart-crypto' ? (
        <DemoCoinTable
          searchQuery={searchQuery}
          hideAssets={hideAssets}
          selectedValue={derivedSelected}
          setupdatePlanMode={setupdatePlanMode}
        />
      ) : (
        <EnhancedTable
          searchQuery={searchQuery}
          hideAssets={hideAssets}
          selectedValue={derivedSelected}
          setupdatePlanMode={setupdatePlanMode}
          onPlanChange={handlePlanChange}
        />
      )}
    </Box>
  );
};

export default CoinBreakdown;
